import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './TicketChats.css';
import { fmtDateTime } from '../lib/when';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * The calls that happened on this ticket, and what was typed in them.
 *
 * WHY THIS EXISTS, which is worth stating because it looks like a duplicate
 * of the Comments fold and is not.
 *
 * A ticket has carried two conversations in two tables. The thread on the
 * chat screen is ticket_updates - literally the rows the Comments fold shows,
 * which is what "one thread, two views" was meant to mean. What gets typed
 * DURING a call is ticket_session_chat, and until now it was reachable only
 * through GET /sessions/:sessionId/chat, by a caller holding a session id
 * that nobody has once the call is over.
 *
 * So the ticket showed one half of a conversation. The other half was stored,
 * whole and intact, behind a URL nobody could get back to. Gordon read that
 * from the ticket as "only part of the chat conversation is being saved",
 * which is exactly what it looked like.
 *
 * A CALL IS NOT TWENTY COMMENTS. His instruction was "instead of falling
 * under comments chat recordings should fall under chats separately", and the
 * shape follows from the thing itself: a call happened at a time, between
 * named people, for a length, with a screen shared or not, and a transcript.
 * Flattening that into the comment stream would lose every part of it except
 * the words - and would bury a two-line comment thread under a hundred lines
 * of "ok", "try now", "still frozen".
 *
 * NOTHING IS WRITTEN HERE. A call's transcript is closed when the call ends.
 * This reads.
 */

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString('en-GB', { timeZone: 'Europe/Malta',
    day: '2-digit',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit'
  });
};

const timeOnly = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('en-GB', { timeZone: 'Europe/Malta', hour: '2-digit', minute: '2-digit' });
};

/** "11 min", or "under a minute" rather than the bare "0 min" that reads as a bug. */
const lasted = (session) => {
  const from = new Date(session.created_at);
  const to = session.ended_at ? new Date(session.ended_at) : null;
  if (!to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const mins = Math.round((to - from) / 60000);
  return mins < 1 ? 'under a minute' : `${mins} min`;
};

const sharedLine = (participants) => {
  const shared = (participants || []).filter((p) => Number(p.shared_seconds) > 0);
  if (shared.length === 0) return null;
  return shared
    .map((p) => `${p.display_name} shared their screen for `
      + `${Math.max(1, Math.round(Number(p.shared_seconds) / 60))} min`)
    .join('; ');
};

const TicketChats = ({ ticketId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which transcripts are expanded. Shut by default: the useful thing at a
  // glance is that a call happened and who was in it, and a long transcript
  // pushes the next call off the screen.
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    if (!ticketId) { setLoading(false); return; }
    try {
      // The interceptor attaches whichever token this caller has; a
      // contractor on a shared ticket reaches this route the same way.
      const r = await axios.get(`${API_BASE}/tickets/${ticketId}/sessions`);
      setSessions(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (err) {
      setError('Could not read the calls on this ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const total = sessions.reduce((n, s) => n + (s.messages?.length || 0), 0);

  const panel = (inner) => (
    <section className="tl-panel tc-panel">
      <div className="tl-head">
        <h3 className="tl-title">Chats</h3>
        {sessions.length > 0 && (
          <span className="tl-count">
            {sessions.length} call{sessions.length === 1 ? '' : 's'}
            {total > 0 && `, ${total} message${total === 1 ? '' : 's'}`}
          </span>
        )}
      </div>
      {inner}
    </section>
  );

  if (loading) return panel(<p className="tl-quiet">Loading chats&hellip;</p>);
  if (error) return panel(<p className="tl-error" role="alert">{error}</p>);
  if (sessions.length === 0) {
    return panel(
      <p className="tl-quiet">
        No calls on this ticket yet. Anything typed during a call is kept here,
        separately from the comments.
      </p>
    );
  }

  return panel(
    <ol className="tc-list">
      {sessions.map((s) => {
        const names = (s.participants || []).map((p) => p.display_name).filter(Boolean);
        const msgs = s.messages || [];
        const shown = open[s.id];
        const length = lasted(s);
        const share = sharedLine(s.participants);
        return (
          <li key={s.id} className="tc-call" data-live={s.state === 'open' ? '' : undefined}>
            <div className="tc-call-head">
              <time className="tc-when" dateTime={s.created_at}
                    title={fmtDateTime(s.created_at)}>
                {when(s.created_at)}
              </time>
              <span className="tc-who">{names.join(', ') || 'Nobody joined'}</span>
              {s.state === 'open'
                ? <span className="tc-badge tc-live">In progress</span>
                : length && <span className="tc-badge">{length}</span>}
            </div>

            {/* The screen-share line is the answer to "who looked at whose
                machine, on which ticket, when" - the question this whole
                table exists for in a firm that bills for it. */}
            {share && <p className="tc-shared">{share}</p>}
            {!share && s.state !== 'open' && (
              <p className="tc-shared tc-none">No screen was shared</p>
            )}
            {s.end_reason && <p className="tc-reason">{s.end_reason}</p>}

            {msgs.length === 0 ? (
              <p className="tc-empty">Nothing was typed during this call.</p>
            ) : (
              <>
                <button
                  type="button"
                  className="tc-toggle"
                  aria-expanded={Boolean(shown)}
                  onClick={() => setOpen((o) => ({ ...o, [s.id]: !o[s.id] }))}
                >
                  {shown ? 'Hide' : 'Show'} {msgs.length} message
                  {msgs.length === 1 ? '' : 's'}
                </button>
                {shown && (
                  <ol className="tc-msgs">
                    {msgs.map((m) => (
                      <li key={m.seq} className="tc-msg">
                        <span className="tc-msg-who">{m.author}</span>
                        <span className="tc-msg-body">{m.body}</span>
                        <time className="tc-msg-at" dateTime={m.created_at}
                              title={fmtDateTime(m.created_at)}>
                          {timeOnly(m.created_at)}
                        </time>
                      </li>
                    ))}
                  </ol>
                )}
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default TicketChats;
